/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  getPeriodOptions,
  getWeekOptions,
  getActionOptions,
  getYearOptions,
} from "../../utils/filterOptions";
import LHOverlay from "../../components/LHOverlay";
import WelcomeContainer from "./components/WelcomeContainer";
import SiteEvalMetrics from "./components/SiteEvalMetrics";
import ActiveItemsTable from "./components/ActiveItemsTable";
import { useSelector, useDispatch } from "react-redux";
import {
  View,
  ScrollView,
  Text,
  StyleSheet,
  Dimensions,
  Platform,
  ActivityIndicator,
  RefreshControl,
} from "react-native";
import LhToggleSwitch from "../../components/LhToggleSwitch";
import { DropDownOption } from "../../components/LHFilter";
import MobileFilterOverlay from "../../components/MobileFilterOverlay";
// Cross-platform import for Victory components
let VictoryPie, VictoryContainer, VictoryLabel;
if (Platform.OS === "web") {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer, VictoryLabel } = require("victory"));
} else {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer, VictoryLabel } = require("victory-native"));
}
import SiteEvalRedirectScreen from "./SiteEvalRedirectScreen";
import SiteEvalDataTable from "./SiteEvalDataTable";
import { createApiClient, apiClient, getTimezoneOffsetString } from "../../api/apiClient";
import {
  setTableData,
  setRecommendedActionPlan,
  setSelectedSiteEvaluation,
} from "../../store/slices/siteEvaluationSlice";
import { RootState, AppDispatch } from "../../store";
import {
  fetchAllQuestionsData,
  mapApiPreWorkDataToAppFormat,
  setPreWorkAnswers,
} from "../../store/siteEvalRedirectSlice";
import { formatUserName } from "../../utils/emailTouserName";
import { formatDate } from "../../utils/dateFormatter";
import {
  handleSiteEvaluationErrorWithFriendlyMessage,
  safeSiteEvaluationApiCall,
  SITE_EVALUATION_API_NAMES,
} from "../../store/utils/siteEvaluationErrorHandler";
import SiteEvaluationErrorModal from './components/SiteEvaluationErrorModal';
import { useTheme } from "../../theme";
interface SiteEvaluationScreenProps {
  onBackPress: () => void;
  onHomePress?: () => void; // Added for home navigation
  siteEvalClick: () => void;
  activeItemsClick: () => void; // Updated prop name to match App.tsx
  onActionPlanPress?: () => void; // Navigation to Action Plan module
}
export const isMobileView = () => {
  const windowWidth = Dimensions.get("window").width;
  return (
    Platform.OS === "ios" || Platform.OS === "android" || windowWidth < 768
  );
};

// Check if device is iOS mobile (not iPad)
export const isIOSMobile = () => {
  const windowWidth = Dimensions.get("window").width;
  return Platform.OS === "ios" && windowWidth < 768
};

// Pie chart data can be static or moved to redux if needed
export const pieChartData = [
  { x: "Yes", y: 45, color: "#4CAF50" },
  { x: "No", y: 30, color: "#F44336" },
  { x: "N/A", y: 25, color: "#9E9E9E" },
];

const chartSize = isMobileView() ? 180 : 220;
const innerRadius = isMobileView() ? 50 : 70;

const isMobile = Dimensions.get("window").width < 768;

const SiteEvaluation: React.FC<SiteEvaluationScreenProps> = ({
  siteEvalClick,
  onBackPress,
  onHomePress,
  activeItemsClick,
  onActionPlanPress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [showRedirectScreen, setShowRedirectScreen] = useState(false);
  // Loading states for API calls
  const [isLoadingSiteEval, setIsLoadingSiteEval] = useState(false);
  // Status message state for success/error notifications
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  // Mobile filter overlay state
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const dispatch: AppDispatch = useDispatch();
  // Get table data and recommended action plan from Redux
  const tableData = useSelector((state: any) => state.siteEvaluation.tableData);
  const RecData = useSelector(
    (state: any) => state.siteEvaluation.recommendedActionPlan
  );
  console.log("RecData", RecData);
  const workData = useSelector((state: any) => state.siteEvalRedirect.workData);
  // Function to show status messages instead of toast
  const showStatusMessage = (type: "success" | "error", message: string) => {
    // Clear any existing message first
    setStatusMessage(null);

    // Set timeout to ensure UI updates before showing new message
    setTimeout(() => {
      setStatusMessage({ type, message });

      // Auto-dismiss success messages after 7 seconds to ensure visibility
      if (type === "success") {
        setTimeout(() => {
          setStatusMessage(null);
        }, 5000);
      }
    }, 100);
  };
  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );

  const userInfo = useSelector((state: RootState) => state.appData?.userInfo);
  const userRoleName = userInfo?.roleName;
  const userRoleId = userInfo?.roleId;

  // For redirect answers
  const siteEvalRedirectDispatch = useDispatch();
  // Import actions from siteEvalRedirectSlice
  const {
    setWalkAnswer,
    setWalkOverlayData,
    setCloseOutAnswer,
    resetSiteEvalRedirect,
    addSiteEvalActionPlan,
    updateSiteEvalActionPlan,
    clearSiteEvalActionPlans,
  } = require("../../store/siteEvalRedirectSlice");
  // Removed setSiteEvaluators - no longer fetching from API
  // Fetch and store site evaluation data
  const fetchSiteEvaluationData = async () => {
    setIsLoadingSiteEval(true);
    try {
      const api = createApiClient();
      const url = `/site-evaluation-enhanced?location=${encodeURIComponent(selectedLocation?.displayText || '')}&sort_by=created_at&sort_order=DESC&pageNumber=1&pageSize=100`;
      const response = await safeSiteEvaluationApiCall<any>((config?: any) => apiClient(api, url, "GET", undefined, config), SITE_EVALUATION_API_NAMES.FETCH_TABLE, true, { location: selectedLocation?.displayText });

      if (!response) {
        // Error already handled by centralized handler
        return;
      }

      // Normalize response shape: apiClient sometimes returns { data: [...] } or the array directly
      const apiData = (response?.data ?? response) || [];
      
      // Map API fields to Redux type
      const mappedTableData = apiData.map((item: any) => {
        // Capitalize status properly (e.g., "in_progress" -> "In Progress", "completed" -> "Completed")
        const formattedStatus = item.STATUS
          ? item.STATUS.split('_')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
          : '';
        
        // Determine if all action plans are completed
        // If completed_action_plans equals total_action_plans (even if both are 0), show Completed
        const isAllActionPlansCompleted = 
          item.completed_action_plans === item.total_action_plans;
        
        return {
        Id: item.ID,
        CreatedBy: formatUserName(item.CREATED_BY_EMAIL),
        Created: item.CREATED_AT,
        Site: item.LOCATION,
        Modified: item.UPDATED_AT,
        Period: item.PERIOD,
        Week: item.WEEK,
        // Normalize year on the row for filtering (keep both lowercase and uppercase keys)
        year: item.CREATED_AT ? new Date(item.CREATED_AT).getFullYear().toString() : "",
        Year: item.CREATED_AT ? new Date(item.CREATED_AT).getFullYear().toString() : "",
        Status: formattedStatus, // Properly capitalized status
        CountOfActionPlans: `${item.completed_action_plans || 0}/${item.total_action_plans || 0}`,
        ActionPlanStatus: isAllActionPlansCompleted ? "Completed" : "In Progress",
        Is_Active_Item: isAllActionPlansCompleted ? 2 : 1, // 2 = completed, 1 = pending
        Log: "", // If Log is not present in API, set as empty string
        // UI field names for Redux type compatibility
        id: item.ID,
        SiteEvaluatorName: item.CREATED_BY_EMAIL, // Store raw email for filtering
        DateTime: item.CREATED_AT,
        FinalizedDateTime: item.UPDATED_AT,
        EvaluationStatus: formattedStatus,
        // Store raw values for calculations
        totalActionPlans: item.total_action_plans || 0,
        completedActionPlans: item.completed_action_plans || 0,
      };
      });
      dispatch(setTableData(mappedTableData));
    } catch (error) {
      console.error("Failed to fetch site evaluation data:", error);
      showStatusMessage(
        "error",
        "Failed to fetch site evaluation data. Please try again."
      );
    } finally {
      setIsLoadingSiteEval(false);
    }
  };

  // Fetch site evaluators for filter dropdown
  // Removed fetchSiteEvaluators effect - now extracting from tableData directly

  useEffect(() => {
    fetchSiteEvaluationData();
  }, [dispatch, selectedLocation]);

  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      await Promise.race([fetchSiteEvaluationData(), timeout]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [toggleStates, setToggleStates] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isOverlayLoading, setIsOverlayLoading] = useState(false);
  const [walkAnswers, setWalkAnswers] = useState<any[]>([]);
  const [walkOverlayData, setWalkOverlayDataState] = useState<any[]>([]);
  const [siteEvalDetailData, setSiteEvalDetailData] = useState<any | null>(null);

  const handleActionPlanChange = (id: number) => {
    setToggleStates((prev) => {
      const newState = !prev[id];
      console.log(
        `Row ${id} toggle changed to ${newState ? "enabled" : "disabled"}`
      );
      return { ...prev, [id]: newState };
    });
  };

  const handleRowClick = async (rowData: any, colIdx?: number) => {
    // Immediately set selected evaluation and redirect to avoid lag
    // The redirect screen will handle all API calls and data loading
    if (colIdx === 0) {
      dispatch(setSelectedSiteEvaluation(rowData));
      setShowRedirectScreen(true);
      return;
    }
    
    // For other columns, still show the overlay (non-blocking)
    setSelectedRowData(rowData);
    setOverlayVisible(true);
    setIsOverlayLoading(true);
    setSiteEvalDetailData(null);
    dispatch(setRecommendedActionPlan([]));
    try {
      // Check if this is a completed evaluation to skip org context params
      // For completed evaluations, we want the original question set (frozen at completion time)
      // For in-progress evaluations, we want questions filtered by current location org context
      const isCompletedEvaluation = rowData.Status === "Completed";
      
      console.log(
        "[Site Evaluation] Loading questions - Status:",
        rowData,
        "isCompletedEvaluation:",
        isCompletedEvaluation
      );
      
      // Also fetch questions data for immediate use in overlay
      const questionsApi = createApiClient();
      // new → org context from interceptor; in-progress → org context + recordId; completed → skip org context
      const locationParam = selectedLocation?.displayText ? `location=${encodeURIComponent(selectedLocation.displayText)}` : '';
      const recordIdParam = !isCompletedEvaluation && rowData.Id ? `recordId=${rowData.Id}` : '';
      const queryParams = [locationParam, recordIdParam].filter(Boolean).join('&');
      const questionsUrl = `/site-evaluation-enhanced/questions/all${queryParams ? `?${queryParams}` : ''}`;
      const questionsResponse = await safeSiteEvaluationApiCall<any>(
        (config?: any) => apiClient(questionsApi, questionsUrl, 'GET', undefined, config),
        SITE_EVALUATION_API_NAMES.FETCH_QUESTIONS,
        true,
        { location: selectedLocation?.displayText },
        isCompletedEvaluation ? { skipOrgContext: true } : undefined
      );
      if (!questionsResponse) return;
      const questionsArray = questionsResponse?.data ?? questionsResponse ?? [];

      // Fetch site-evaluation-enhanced/{id} for overlay
      const api = createApiClient();
      const clientTimestamp = new Date().toISOString();
      const clientTimezone = getTimezoneOffsetString();
      const response = await safeSiteEvaluationApiCall<any>((config?: any) => apiClient(api, `/site-evaluation-enhanced/${rowData.Id}?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`, "GET", undefined, config), SITE_EVALUATION_API_NAMES.FETCH_BY_ID, true, { id: rowData.Id });
      if (!response) return;
      
      // Extract data from the new response structure
      const responseData = response?.data;
      setSiteEvalDetailData(responseData);
      
      // Prepare walkAnswers and walkOverlayData arrays
      const walkAnswersArr: any[] = [];
      const walkOverlayArr: any[] = [];
      let preWorkAnswersArr: any[] = [];
      
      // Map walk data from the new API structure
      if (responseData?.walk && Array.isArray(responseData.walk)) {
        responseData.walk.forEach((walkItem: any) => {
          let mappedAnswer;
          if (walkItem.response === 1) mappedAnswer = "yes";
          else if (walkItem.response === 2) mappedAnswer = "no";
          else if (walkItem.response === 3) mappedAnswer = "na";
          else mappedAnswer = undefined;
          
          const walkData = {
            Data_Id: walkItem.question_id,
            Response: walkItem.response,
            Section_Type: "Data",
            mappedAnswer
          };
          walkAnswersArr.push(walkData);
          siteEvalRedirectDispatch(
            setWalkAnswer({ id: walkItem.question_id.toString(), answer: mappedAnswer })
          );
        });
      }
      
      // Helper function to map section data
      const mapSectionData = (sectionData: any[], sectionName: string) => {
        if (sectionData && Array.isArray(sectionData)) {
          sectionData.forEach((item: any) => {
            let mappedAnswer;
            if (item.response === 1) mappedAnswer = "yes";
            else if (item.response === 2) mappedAnswer = "no";
            else if (item.response === 3) mappedAnswer = "na";
            else mappedAnswer = undefined;
            
            // Check if there's action plan data - if yes, default answer to "yes" if not set
            const hasActionPlan = !!(item.action_plan || item.supervisor || item.comment);
            
            // If there's an action plan but no explicit answer, default to "yes" so toggle is on
            if (hasActionPlan && !mappedAnswer) {
              mappedAnswer = "yes";
            }
            
            if (mappedAnswer) {
              siteEvalRedirectDispatch(
                setWalkAnswer({ id: item.question_id.toString(), answer: mappedAnswer })
              );
            }
            
            // If there's action plan data, map overlay data
            if (hasActionPlan) {
              siteEvalRedirectDispatch(
                setWalkOverlayData({
                  id: item.question_id.toString(),
                  actionPlan: item.action_plan || item.comment || "",
                  supervisor: item.supervisor || "",
                  section_value: item.section_value || "",
                  parent_section_value: item.parent_section_value || "",
                  status: item.status || "",
                })
              );
              
              // Store in Redux so SiteEvalActionPlanOverlay can access it
              // Store for all records (view-only mode is handled at form level)
              const questionId = item.question_id.toString();
              // Create unique key with evaluation ID
              const uniqueKey = `${rowData.Id}_${questionId}`;
              
              const actionPlanData = {
                id: `${rowData.Id}_${questionId}`,
                localId: Date.now() + parseInt(questionId),
                itemId: uniqueKey,
                relatedId: uniqueKey,
                siteEvaluationId: rowData.Id,
                questionId: questionId,
                title: item.section_value || item.parent_section_value || 'Site Evaluation Action Plan',
                actionPlan: item.action_plan || item.comment || "",
                supervisor: item.supervisor || "",
                assignee: item.supervisor || "",
                source: 'Site Evaluation',
                type: null,
                priority: null,
                startDate: null,
                targetDate: null,
                notes: "",
                frontlineInvolvement: false,
                employee: null,
                employeeName: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isReference: false,
                status: item.status || 'Active'
              };
              
              // Dispatch to Redux - will update if exists or add new
              siteEvalRedirectDispatch(updateSiteEvalActionPlan(actionPlanData));
              console.log('[SiteEvaluation] Stored action plan in Redux for question:', questionId);
            }
          });
        }
      };
      
      // Map all other section data
      mapSectionData(responseData?.vpe_mhe, "vpe_mhe");
      mapSectionData(responseData?.yard_gate, "yard_gate");
      mapSectionData(responseData?.layer_redzone, "layer_redzone");
      mapSectionData(responseData?.pick_area, "pick_area");
      mapSectionData(responseData?.deep_reserve, "deep_reserve");
      mapSectionData(responseData?.regulatory, "regulatory");
      mapSectionData(responseData?.lighthouse, "lighthouse");
      
      // Map action plans to recommended action plan format
      if (responseData?.action_plans && Array.isArray(responseData.action_plans)) {
        const normalizeRecommendedActionPlanStatus = (raw: any): string => {
          const v = String(raw ?? '').trim();
          const lower = v.toLowerCase();
          if (lower === 'in_progress' || lower === 'in progress' || lower === 'inprogress') return 'In Progress';
          if (lower === 'completed') return 'Completed';
          if (!v) return '';
          return v;
        };

        // Helper function to resolve parent section name from question_id
        const getParentSectionValue = (questionId: number | string): string => {
          if (!questionId || !questionsResponse) return "";
          
          // Find the question by ID
          const question = questionsResponse.find((q: any) => q.ID === questionId.toString());
          if (!question) return "";
          
          // Get parent ID
          const parentId = question.Parent_Id;
          if (!parentId || parentId === "NULL") return "";
          
          // Find parent question
          const parentQuestion = questionsResponse.find((q: any) => q.ID === parentId);
          if (!parentQuestion) return "";
          
          // Return parent's Section_Value
          return parentQuestion.Section_Value || "";
        };

        const mappedRecommendedActionPlanArr = responseData.action_plans.map((actionPlan: any) => {
          // Get question_id from capabilities array
          const questionId = actionPlan.capabilities?.[0]?.question_id;
          const sectionValue = questionId ? getParentSectionValue(questionId) : "";
          
          return {
            Section: sectionValue || actionPlan.title || actionPlan.type || "",
            ActionPlan: actionPlan.action_plan_description || actionPlan.notes || "",
            "Assigned Supervisor": formatUserName(actionPlan.assignee_email) || actionPlan.assignee_gpid || "",
            ActionPlanStatus: normalizeRecommendedActionPlanStatus(actionPlan.status || actionPlan.assignment_status || ""),
          };
        });
        dispatch(setRecommendedActionPlan(mappedRecommendedActionPlanArr));
        
        // Store action plans in Redux for editing
        responseData.action_plans.forEach((actionPlan: any) => {
            // Get question_id from capabilities array
            const questionId = actionPlan.capabilities?.[0]?.question_id?.toString();
            
            if (questionId && actionPlan.action_plan_description) {
              // Create unique key with evaluation ID to prevent cross-contamination
              const uniqueKey = `${rowData.Id}_${questionId}`;
              
              // Map type string to dropdown option object
              let typeObj = null;
              if (actionPlan.type) {
                const typeStr = actionPlan.type.toLowerCase();
                // Match with known type options
                if (typeStr === 'kpi') {
                  typeObj = { id: 'kpi', displayText: 'KPI' };
                } else if (typeStr === 'kfa') {
                  typeObj = { id: 'kfa', displayText: 'KFA' };
                } else if (typeStr === 'process') {
                  typeObj = { id: 'process', displayText: 'PROCESS' };
                } else {
                  // Fallback to using the value as-is
                  typeObj = { id: typeStr, displayText: actionPlan.type };
                }
              }
              
              // Map priority string to dropdown option object
              let priorityObj = null;
              if (actionPlan.priority) {
                const priorityStr = actionPlan.priority.toLowerCase();
                if (priorityStr === 'high') {
                  priorityObj = { id: 'high', displayText: 'High' };
                } else if (priorityStr === 'medium') {
                  priorityObj = { id: 'medium', displayText: 'Medium' };
                } else if (priorityStr === 'low') {
                  priorityObj = { id: 'low', displayText: 'Low' };
                } else {
                  priorityObj = { id: priorityStr, displayText: actionPlan.priority };
                }
              }
              
              const actionPlanData = {
                id: actionPlan.id ? `action_plan_${actionPlan.id}` : `${rowData.Id}_${questionId}`,
                localId: Date.now() + parseInt(questionId),
                itemId: uniqueKey,
                relatedId: uniqueKey,
                siteEvaluationId: rowData.Id,
                questionId: questionId,
                title: actionPlan.title || 'Site Evaluation Action Plan',
                actionPlan: actionPlan.action_plan_description || "",
                supervisor: actionPlan.assignee_email || "",
                assignee: actionPlan.assignee_email || "",
                selectedAssignee: actionPlan.assignee_email ? {
                  id: actionPlan.assignee_email,
                  email: actionPlan.assignee_email,
                  gpid: actionPlan.assignee_gpid || "",
                  role: actionPlan.assignee_role || "",
                  roleId: actionPlan.assignee_role_id || 0,
                  displayText: formatUserName(actionPlan.assignee_email) || actionPlan.assignee_email
                } : null,
                source: 'Site Evaluation',
                type: typeObj,
                priority: priorityObj,
                startDate: actionPlan.start_date ? new Date(actionPlan.start_date) : null,
                targetDate: actionPlan.target_date ? new Date(actionPlan.target_date) : null,
                notes: actionPlan.notes || "",
                frontlineInvolvement: actionPlan.frontline_employee_involvement || false,
                employee: actionPlan.involved_employee_gpids || null,
                employeeName: actionPlan.involved_employee_names || null,
                selectedEmployee: actionPlan.involved_employee_gpids ? {
                  id: actionPlan.involved_employee_gpids,
                  gpid: actionPlan.involved_employee_gpids,
                  name: actionPlan.involved_employee_names || "",
                  displayText: actionPlan.involved_employee_names || ""
                } : null,
                createdAt: actionPlan.created_at || new Date().toISOString(),
                updatedAt: actionPlan.updated_at || new Date().toISOString(),
                isReference: false,
                status: actionPlan.status || 'Active',
                actionPlanId: actionPlan.id, // Store original action plan ID
                rawFormData: {
                  title: actionPlan.title || 'Site Evaluation Action Plan',
                  selectedType: typeObj,
                  selectedPriority: priorityObj,
                  selectedAssignee: actionPlan.assignee_email ? {
                    id: actionPlan.assignee_email,
                    email: actionPlan.assignee_email,
                    gpid: actionPlan.assignee_gpid || "",
                    role: actionPlan.assignee_role || "",
                    roleId: actionPlan.assignee_role_id || 0,
                    displayText: formatUserName(actionPlan.assignee_email) || actionPlan.assignee_email
                  } : null,
                  startDate: actionPlan.start_date ? new Date(actionPlan.start_date) : null,
                  targetDate: actionPlan.target_date ? new Date(actionPlan.target_date) : null,
                  actionPlan: actionPlan.action_plan_description || "",
                  notes: actionPlan.notes || "",
                  frontlineInvolvement: actionPlan.frontline_employee_involvement || false,
                  selectedEmployee: actionPlan.involved_employee_gpids ? {
                    id: actionPlan.involved_employee_gpids,
                    gpid: actionPlan.involved_employee_gpids,
                    name: actionPlan.involved_employee_names || "",
                    displayText: actionPlan.involved_employee_names || ""
                  } : null,
                }
              };
              
              // Dispatch to Redux - will update if exists or add new
              siteEvalRedirectDispatch(updateSiteEvalActionPlan(actionPlanData));
              console.log('[SiteEvaluation] Stored action plan in Redux from action_plans array for question:', questionId);
          }
        });
        
        // Extract involved employees for close out review
        const involvedEmployees = responseData.action_plans
          .filter((actionPlan: any) => actionPlan.involved_employee_names)
          .map((actionPlan: any) => actionPlan.involved_employee_names)
          .join(",");
          
        if (involvedEmployees) {
          siteEvalRedirectDispatch(
            setCloseOutAnswer({ id: 2, partner: involvedEmployees })
          );
        }
      }
      
      // Map prework data from the new API structure
      if (responseData?.prework && Array.isArray(responseData.prework)) {
        // Get prework questions data to map the section values
        const preWorkQuestionsData = questionsResponse.filter((q: any) => 
          q.ID >= "1000" && q.ID < "2000" && q.Section_Type === "DATA"
        );
        
        const mappedPreWorkData = responseData.prework
          .map((preWorkItem: any) => {
            // Find the corresponding question to get the review text
            const questionData = preWorkQuestionsData.find((q: any) => 
              q.ID === preWorkItem.question_id.toString()
            );
            
            // Only include if we found a matching DATA question
            if (!questionData) {
              return null;
            }
            
            return {
              id: preWorkItem.question_id.toString(),
              priorVisit: preWorkItem.prior_visit !== undefined && preWorkItem.prior_visit !== null && !isNaN(Number(preWorkItem.prior_visit))
                ? Number(Number(preWorkItem.prior_visit).toFixed(2))
                : 0,
              lcp: preWorkItem.lcp !== undefined && preWorkItem.lcp !== null && !isNaN(Number(preWorkItem.lcp))
                ? Number(Number(preWorkItem.lcp).toFixed(2))
                : 0,
              ytd: preWorkItem.ytd !== undefined && preWorkItem.ytd !== null && !isNaN(Number(preWorkItem.ytd))
                ? Number(Number(preWorkItem.ytd).toFixed(2))
                : 0,
              review: questionData?.Section_Value || `Question ${preWorkItem.question_id}`,
            };
          })
          .filter((item: any) => item !== null); // Remove null entries (non-DATA items)
        
        siteEvalRedirectDispatch(setPreWorkAnswers(mappedPreWorkData));
      }
      
      setWalkAnswers(walkAnswersArr);
      setWalkOverlayDataState(walkOverlayArr);
      
    } catch (error) {
      console.error("Failed to fetch site evaluation details:", error);
      try {
        handleSiteEvaluationErrorWithFriendlyMessage(SITE_EVALUATION_API_NAMES.FETCH_BY_ID, error, true, { id: selectedRowData?.Id });
      } catch (e) {
        console.error('Error while dispatching SiteEvaluation centralized handler:', e);
      }
      showStatusMessage(
        "error",
        "Failed to fetch site evaluation details. Please try again."
      );
    } finally {
      setIsOverlayLoading(false);
    }
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setIsOverlayLoading(false);
    setSelectedRowData(null);
    setRecommendedActionPlan([]);
    setSiteEvalDetailData(null);
    // Reset siteEvalRedirect store when overlay is closed
    siteEvalRedirectDispatch(resetSiteEvalRedirect());
  };

  // Map API fields to UI columns
  const tableColumns = [
    { columnKey: "CreatedBy", header: "Site Evaluator Name" },
    // { columnKey: "Created", header: "Created Time" },
    { columnKey: "Site", header: "Site" },
    {
      columnKey: "Modified",
      header: "Finalized Time",
      renderCell: (item: any) => (
        <Text style={{ color: "rgb(97, 97, 97)" }}>
          {item.Modified ? formatDate(item.Modified) : "-"}
        </Text>
      ),
    },
    {
      columnKey: "periodWeek",
      header: "Period Week",
      getSortValue: (item: any) => {
        const p = Number(item.Period) || 0;
        const w = Number(item.Week) || 0;
        return p * 100 + w;
      },
      renderCell: (item: any) => (
        <Text style={{ color: "rgb(97, 97, 97)" }}>
          {item.Period && item.Week ? `Period${item.Period}Week${item.Week}` : "-"}
        </Text>
      ),
    },
    { columnKey: "Status", header: "Status" },
    { columnKey: "CountOfActionPlans", header: "Count Of Action Plans" },
    {
      columnKey: "ActionPlanStatus", // This field is computed from action plan counts
      header: "Action Plan Status",
      type: "toggle" as const,
      width: 210,
    },
    // { columnKey: "Log", header: "Log" }
  ];

  const handleRedirectBack = () => {
    setShowRedirectScreen(false);
    // Refetch data when coming back from redirect screen
    fetchSiteEvaluationData();
  };
  // --- FILTER STATE LOGIC ---
  // Define default filter state (keys should match your filter keys)
  const defaultFilters = {
    CreatedBy: "",
    status: "",
    period: "",
    week: "",
    year: "",
  };

  const [filters, setFilters] = useState<Record<string, any>>({ ...defaultFilters });
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({ ...defaultFilters });
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  // Extract site evaluator options from tableData (CREATED_BY_EMAIL field)
  const getSiteEvaluatorOptions = (): { id: string; displayText: string; email: string }[] => {
    console.log("Extracting Site Evaluator options from tableData:", tableData);
    
    // Get unique CREATED_BY_EMAIL values from tableData
    const uniqueEmails = new Set<string>();
    (tableData || []).forEach((item: any) => {
      const email = item.SiteEvaluatorName || item.CreatedBy || item.CREATED_BY_EMAIL;
      if (email && email !== '') {
        uniqueEmails.add(email);
      }
    });
    
    // Convert to options array with formatted display text
    const options = Array.from(uniqueEmails).map((email) => ({
      id: email, // Use raw email as ID (sent to backend)
      displayText: formatUserName(email), // Display formatted name
      email: email,
    }));
    
    console.log("Site Evaluator Options:", options);
    return options;
  };

  const filterOptions = [
    {
      key: "CreatedBy",
      label: "Site Evaluator Name",
      selectedOptionId: filters.CreatedBy,
      options: getSiteEvaluatorOptions(),
    },
    {
      key: "status",
      label: "Status",
      selectedOptionId: filters.status,
      options: [
        { id: "In Progress", displayText: "In Progress" },
        { id: "Completed", displayText: "Completed" },
      ],
    },
    {
      key: "period",
      label: "Period",
      selectedOptionId: filters.period,
      options: getPeriodOptions()
    },
    {
      key: "week",
      label: "Week",
      selectedOptionId: filters.week,
      options: getWeekOptions()
    },
    {
      key: "year",
      label: "Year",
      selectedOptionId: filters.year,
      options: getYearOptions(),
    },
  ];

  // Handle filter value change
  const handleFilterChange = (
    key: string,
    value: DropDownOption | DropDownOption[] | null
  ) => {
    // Normalize selected value properly based on the filter type
    let newVal = "";
    
    if (Array.isArray(value)) {
      // Multi-select (if supported)
      newVal = value.map((v: any) => v.id).join(",");
    } else if (value && typeof value === "object") {
      // Single dropdown selection
      newVal = String(value.id || "");
    } else if (value === null || value === undefined) {
      // Clear selection
      newVal = "";
    } else {
      // Direct string value
      newVal = String(value);
    }

    // Update filters state
    const newFilters = { ...filters, [key]: newVal };
    setFilters(newFilters);

    console.log(`Filter changed: ${key} = ${newVal}`, newFilters);
    // Note: The debounced useEffect will handle the API call
  };

  // Debounce timer ref for auto-applying server filters
  const filtersDebounceRef = useRef<any>(null);

  // Compute displayed table data: just use the data from API response (already filtered)
  // No client-side filtering needed since the API handles all filtering
  const displayedTableData = useMemo(() => {
    return tableData || [];
  }, [tableData]);

  // --- Place filter logic after selectedLocation/dispatch are defined ---

  // Apply filters: call API with selected filters. Accept optional filters parameter
  const applyFilters = React.useCallback(
    async (filtersToUse?: Record<string, any>) => {
      setIsApplyingFilters(true);
      const toApply = filtersToUse ? { ...filtersToUse } : { ...filters };
      setIsLoadingSiteEval(true);
      try {
        const api = createApiClient();
        
        // Build query parameters from filters (excluding empty/null values)
        const queryParams: Record<string, string> = {};
        
        Object.entries(toApply).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            // Handle different filter types properly
            if (key === "CreatedBy") {
              // Site evaluator filter - use email format for API
              queryParams["created_by_email"] = String(value);
            } else if (key === "status") {
              // Status filter - normalize to match API expectations
              const normalizedStatus = String(value).toLowerCase().replace(/\s+/g, "_");
              queryParams["status"] = normalizedStatus;
            } else if (key === "period") {
              queryParams["period"] = String(value);
            } else if (key === "week") {
              queryParams["week"] = String(value);
            } else if (key === "year") {
              queryParams["year"] = String(value);
            }
          }
        });
        
        // Construct query string
        const queryString = Object.entries(queryParams)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join("&");
        
        const url = `/site-evaluation-enhanced?location=${encodeURIComponent(selectedLocation?.displayText || '')}${
          queryString ? `&${queryString}` : ""
        }&sort_by=created_at&sort_order=DESC&pageNumber=1&pageSize=100`;
        
        console.log("Applied filters:", toApply);
        console.log("Query params:", queryParams);
        console.log("API URL:", url);
        
        const response = await apiClient(api, url, "GET");
        const apiData = response?.data || [];
        
        // Map API response to table format
        const mappedTableData = apiData.map((item: any) => {
          // Capitalize status properly
          const formattedStatus = item.STATUS
            ? item.STATUS.split('_')
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
            : '';
          
          // Determine if all action plans are completed
          const isAllActionPlansCompleted = 
            item.completed_action_plans === item.total_action_plans;
          
          return {
            Id: item.ID,
            CreatedBy: formatUserName(item.CREATED_BY_EMAIL),
            Created: item.CREATED_AT,
            Site: item.LOCATION,
            Modified: item.UPDATED_AT,
            Period: item.PERIOD,
            Week: item.WEEK,
            year: item.CREATED_AT ? new Date(item.CREATED_AT).getFullYear().toString() : "",
            Year: item.CREATED_AT ? new Date(item.CREATED_AT).getFullYear().toString() : "",
            Status: formattedStatus,
            CountOfActionPlans: `${item.completed_action_plans || 0}/${item.total_action_plans || 0}`,
            ActionPlanStatus: isAllActionPlansCompleted ? "Completed" : "In Progress",
            Is_Active_Item: isAllActionPlansCompleted ? 2 : 1,
            Log: "",
            id: item.ID,
            SiteEvaluatorName: item.CREATED_BY_EMAIL, // Store raw email for filtering
            DateTime: item.CREATED_AT,
            FinalizedDateTime: item.UPDATED_AT,
            EvaluationStatus: formattedStatus,
            totalActionPlans: item.total_action_plans || 0,
            completedActionPlans: item.completed_action_plans || 0,
          };
        });
        
        dispatch(setTableData(mappedTableData));
        setAppliedFilters(toApply as Record<string, any>);
        
      } catch (error) {
        console.error("Failed to apply filters:", error);
        showStatusMessage(
          "error",
          "Failed to apply filters. Please try again."
        );
      } finally {
        setIsLoadingSiteEval(false);
        setIsApplyingFilters(false);
      }
    },
    [selectedLocation, dispatch, filters]
  );

  // Auto-apply filters after a short debounce when filters change.
  // This ensures that rapid filter changes don't trigger excessive API calls.
  useEffect(() => {
    // Do nothing if filters equal appliedFilters
    if (JSON.stringify(filters) === JSON.stringify(appliedFilters)) return;
    if (filtersDebounceRef.current) {
      clearTimeout(filtersDebounceRef.current);
    }
    // Debounce server apply (300ms)
    filtersDebounceRef.current = setTimeout(() => {
      applyFilters(filters);
    }, 300);
    return () => {
      if (filtersDebounceRef.current) clearTimeout(filtersDebounceRef.current);
    };
  }, [filters, appliedFilters, applyFilters]);

  // Reset filters: clear filters and call API with no filters
  const resetFilters = React.useCallback(async () => {
    // Clear filters state
    setFilters({ ...defaultFilters });
    // Apply the default (empty) filters which will fetch all data
    await applyFilters(defaultFilters);
  }, [applyFilters]);

  // Mobile filter overlay handlers
  const handleMobileFilterPress = () => {
    setMobileFilterOverlayVisible(true);
  };

  const handleMobileFilterCancel = () => {
    setMobileFilterOverlayVisible(false);
  };

  const handleMobileFilterProceed = (selectedFilters: Partial<Record<string, any>>) => {
    // Merge selected filters into current filters synchronously
    const merged: Record<string, any> = { ...filters };
    Object.entries(selectedFilters).forEach(([key, value]) => {
      // If value is a DropDownOption or array of DropDownOption, normalize to id string
      if (Array.isArray(value)) {
        merged[key] = value.map((v: any) => v.id).join(",");
      } else if (value && typeof value === "object" && "id" in value) {
        merged[key] = value.id;
      } else {
        merged[key] = value as any;
      }
    });

    // Update filters state and trigger immediate server sync
    setFilters(merged);
    // Close overlay
    setMobileFilterOverlayVisible(false);
    // Immediately sync server with merged filters
    applyFilters(merged);
  };

  const isLighthouseExecutionParent = (parentId: any) => {
    return workData.some(
      (wd: any) =>
        wd.id === parentId &&
        wd.SiteEvaluationaForm === "Lighthouse Execution" &&
        wd.toggleOnly === true
    );
  };

  if (showRedirectScreen) {
    return (
      <SiteEvalRedirectScreen
        onBackPress={handleRedirectBack}
        onHomePress={onHomePress}
        showStatusMessage={showStatusMessage}
      />
    );
  }

  // If on iOS mobile and mobile filter overlay is visible, show the overlay as a full screen
  // replacement (no welcome / metrics content) — match ShiftHandOver behavior
  if (isIOSMobile() && isMobileFilterOverlayVisible) {
    return (
      <MobileFilterOverlay
        visible={isMobileFilterOverlayVisible}
        onDismiss={handleMobileFilterCancel}
        filters={filterOptions as any}
        onFilterChange={handleFilterChange}
        onProceed={handleMobileFilterProceed}
        onCancel={handleMobileFilterCancel}
        onReset={() => {
          setMobileFilterOverlayVisible(false);
          resetFilters();
        }}
        currentFilters={filters}
      />
    );
  }

  return (
    <>
    <ScrollView
      style={[styles.container, isDark && { backgroundColor: colors.background.page }]}
      contentContainerStyle={{ flexGrow: 1 }}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          tintColor="#0066CC"
          colors={["#0066CC"]}
          title="Refreshing..." 
        />
      }
    >
      {/* Display status message if present */}
      {statusMessage && (
        <View style={styles.statusMessageContainer}>
          <View
            style={[
              styles.statusMessage,
              statusMessage.type === "error"
                ? styles.errorMessage
                : styles.successMessage,
            ]}
          >
            <Text
              style={[
                statusMessage.type === "error"
                  ? styles.errorText
                  : styles.successText,
                { fontSize: 16, fontWeight: "500" },
              ]}
            >
              {statusMessage.message}
            </Text>
          </View>
        </View>
      )}

      <WelcomeContainer
        onBeginClick={() => {
          // Clear all previous state before starting new evaluation
          dispatch(setSelectedSiteEvaluation(null));
          siteEvalRedirectDispatch(resetSiteEvalRedirect());
          
          // Clear Redux action plans for fresh start
          siteEvalRedirectDispatch(clearSiteEvalActionPlans());
          
          // Fetch fresh questions data
          dispatch(fetchAllQuestionsData(selectedLocation.displayText));
          setShowRedirectScreen(true);
        }}
        isMobileView={isMobileView()}
        styles={styles}
      />

      <SiteEvalMetrics
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        isMobile={isMobile}
        isLoading={isLoadingSiteEval || isApplyingFilters}
        tableData={displayedTableData || []}
        tableColumns={tableColumns}
        onRowClick={handleRowClick}
        styles={styles}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        onToggleChange={(id: number, value: boolean) => {
          // Site eval metrics toggles are display-only, no action needed
        }}
        showMobileFilterButton={isIOSMobile()}
        onMobileFilterPress={handleMobileFilterPress}
        currentFilters={filters}
        onMobileFilterProceed={handleMobileFilterProceed}
        onGoToActionPlan={onActionPlanPress}
      />

      {isOverlayVisible && selectedRowData && (
        <LHOverlay
          visible={isOverlayVisible}
          onDismiss={closeOverlay}
          title="Site Evaluation Details"
          size="large"
          close={true}
          position="center"
          dismissOnClickOutside={true}
          backgroundColor={isDark ? colors.background.elevated : "#F8F9FA"}
        >
          {isOverlayLoading ? (
            <View style={styles.overlayLoadingContainer}>
              <ActivityIndicator size="large" color={isDark ? colors.brand.primary : "#0066CC"} />
              <Text style={[styles.overlayLoadingText, isDark && { color: colors.text.secondary }]}>Loading site evaluation details...</Text>
            </View>
          ) : (
            <View style={[styles.gridContainer, isDark && { backgroundColor: colors.background.elevated }]}>
              <View style={styles.gridRow}>
                {/* Result Summary with Pie Chart */}
                <View style={[styles.gridCard, isDark && { backgroundColor: colors.background.elevated }]}>
                  <Text style={[styles.cardTitle, isDark && { color: colors.text.primary }]}>Result Summary</Text>
                  <View style={styles.chartContainer}>
                    {/* Calculate yes/no/na counts from /site-evaluation-enhanced/{id} response */}
                    {(() => {
                      const detail = siteEvalDetailData;
                      const sectionKeys = [
                        "walk",
                        "vpe_mhe",
                        "yard_gate",
                        "layer_redzone",
                        "pick_area",
                        "deep_reserve",
                        "regulatory",
                        "lighthouse",
                      ] as const;

                      const allItems = sectionKeys.flatMap((key) =>
                        Array.isArray(detail?.[key]) ? detail[key] : []
                      );

                      const yesCount = allItems.filter((a: any) => a?.response === 1).length;
                      const noCount = allItems.filter((a: any) => a?.response === 2).length;
                      const naCount = allItems.filter((a: any) => a?.response === 3).length;

                      const total = yesCount + noCount + naCount;

                      // Use Figma palette for slices and smaller gaps
                      const figmaColorMap: Record<string, string> = {
                        Yes: "#28B5E8",
                        No: "#B76DA5",
                        "N/A": "#007A9A",
                      };
                      const pieData = [
                        {
                          x: "Yes: ",
                          y: total ? Math.round((yesCount / total) * 100) : 0,
                          color: figmaColorMap.Yes,
                        },
                        {
                          x: "No: ",
                          y: total ? Math.round((noCount / total) * 100) : 0,
                          color: figmaColorMap.No,
                        },
                        {
                          x: "N/A: ",
                          y: total ? Math.round((naCount / total) * 100) : 0,
                          color: figmaColorMap["N/A"],
                        },
                      ];

                      // Match NHO donut style: for 180px chart they use innerRadius=80 (thin ring).
                      // Scale the inner radius proportionally for our chartSize.
                      const innerRadius = Math.round((chartSize / 180) * 80);
                      return (
                        <>
                          {/* Fixed-size wrapper gives the absolute center label a concrete
                              reference box. Without it, percentage-based top/left ("50%")
                              fails on iOS because the parent has no explicit height. */}
                          <View style={[{ height: chartSize }, styles.chartSizeWrapper]}>
                          <VictoryPie
                            width={chartSize}
                            height={chartSize}
                            data={pieData}
                            colorScale={pieData.map((item) => item.color)}
                            innerRadius={innerRadius}
                            padAngle={0.1}
                            containerComponent={
                              <VictoryContainer
                                responsive={false}
                                style={{
                                  touchAction: "auto",
                                  userSelect: "auto",
                                }}
                              />
                            }
                            labels={() => null}
                            {...(Platform.OS !== "web"
                              ? {
                                  animate: {
                                    duration: 800,
                                    easing: "bounce",
                                    onLoad: { duration: 500 },
                                  },
                                }
                              : {})}
                            style={{
                              data: {
                                fillOpacity: 0.98,
                                stroke: "#fff",
                                strokeWidth: 0.5,
                              },
                            }}
                          />
                          <View style={styles.centerLabel}>
                            <Text style={[styles.centerLabelText, isDark && { color: colors.text.primary }]}>Total</Text>
                            <Text style={[styles.centerLabelValue, isDark && { color: colors.text.secondary }]}>{total}</Text>
                          </View>
                          </View>
                          <View style={styles.legendContainer}>
                            {pieData.map((item, index) => {
                              const label = String(item.x);
                              const percentStr =
                                label.startsWith("Yes")
                                  ? `${total ? Math.round((yesCount / total) * 100) : 0}%`
                                  : label.startsWith("No")
                                    ? `${total ? Math.round((noCount / total) * 100) : 0}%`
                                    : `${total ? Math.round((naCount / total) * 100) : 0}%`;

                              return (
                                <View key={index} style={styles.legendItem}>
                                  <View
                                    style={[
                                      styles.legendColor,
                                      { backgroundColor: item.color },
                                    ]}
                                  />
                                  <Text style={[styles.legendText, isDark && { color: colors.text.secondary }]}>{label}</Text>
                                    <Text style={[styles.legendText, isDark && { color: colors.text.secondary }]}>{percentStr}</Text>
                                </View>
                              );
                            })}
                          </View>
                        </>
                      );
                    })()}
                  </View>
                </View>
              </View>

              {/* Recommended Action Plan */}
              <View style={[styles.fullWidthCard, isDark && { backgroundColor: colors.background.elevated }]}>
                {/* <Text style={styles.sectionTitle}>Recommended Action Plan</Text> */}
                <View style={[styles.tableContainer, isDark && { backgroundColor: colors.background.elevated, shadowColor: 'transparent' }]}>
                  {/* Render RecData as stacked cards (Figma) instead of a table */}
                  <View style={[styles.cardWrapper, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
                    {/* Blue header that spans the card */}
                    <View style={styles.cardHeader}>
                      <Text style={styles.cardHeaderText}>Recommended Action Plan</Text>
                    </View>

                    {/* Empty state */}
                    {(!RecData || RecData.length === 0) && (
                      <View style={styles.emptyCard}>
                        <Text style={[styles.emptyText, isDark && { color: colors.text.secondary }]}>No recommended actions</Text>
                      </View>
                    )}

                    {/* One action card per recommended action */}
                    {(RecData || []).map((item: any, idx: number) => (
                      <React.Fragment key={idx}>
                        <View style={[styles.actionCard, isDark && { backgroundColor: colors.background.elevated }]}>
                          <View style={styles.actionRow}>
                            <Text style={[styles.actionLabel, isDark && { color: colors.text.primary }]}>Section:</Text>
                            <Text style={[styles.actionValue, isDark && { color: colors.text.secondary }]}>{item.Section || "-"}</Text>
                          </View>
                          <View style={styles.actionRow}>
                            <Text style={[styles.actionLabel, isDark && { color: colors.text.primary }]}>Action Plan:</Text>
                            <Text style={[styles.actionValue, isDark && { color: colors.text.secondary }]}>{item.ActionPlan || "-"}</Text>
                          </View>
                          <View style={styles.actionRow}>
                            <Text style={[styles.actionLabel, isDark && { color: colors.text.primary }]}>Assigned Supervisor:</Text>
                            <Text style={[styles.actionValue, isDark && { color: colors.text.secondary }]}>{formatUserName(item["Assigned Supervisor"]) || "-"}</Text>
                          </View>
                          <View style={styles.actionRow}>
                            <Text style={[styles.actionLabel, isDark && { color: colors.text.primary }]}>Action Plan Status:</Text>
                            <Text style={[styles.actionValue, isDark && { color: colors.text.secondary }]}>{item.ActionPlanStatus ?? "-"}</Text>
                          </View>
                        </View>
                        {idx < (RecData || []).length - 1 && <View style={[styles.cardDivider, isDark && { backgroundColor: colors.border.divider }]} />}
                      </React.Fragment>
                    ))}
                  </View>
                </View>
              </View>
            </View>
          )}
        </LHOverlay>
      )}
    </ScrollView>

    {/* Mount centralized error modal outside of container to avoid clipping issues */}
    <SiteEvaluationErrorModal />
    </>
  );
};

const styles = StyleSheet.create({
  // ...existing styles...
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
  },
  overlayLoadingContainer: {
    minHeight: 280,
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  overlayLoadingText: {
    fontSize: 16,
    color: "#616161",
    fontWeight: "500",
  },
  container: {
    paddingVertical: "1.5%",
    paddingHorizontal: Platform.OS === "web" ? "1.5%" : 16,
    backgroundColor: "#F5F9FF",
    minHeight: "100%",
  },
  welcomeCard: {
    // backgroundColor: "white",
    // borderRadius: 8,
    // marginBottom: 16,
    // borderLeftWidth: 4,
    // borderLeftColor: "#0066CC",
    // shadowColor: "#000",
    // shadowOffset: { width: 0, height: 1 },
    // shadowOpacity: 0.1,
    // shadowRadius: 3,
    // elevation: 2,
  },
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical:16,
    flexWrap: Platform.OS === "web" ? "nowrap" : "wrap",
  },
  welcomeLeftContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  welcomeText: {
    fontSize: 28,
    fontStyle: "normal",
    fontWeight: "700",
    color: "#2D2D2D",
    // marginLeft: 12,
    lineHeight :32,
   
  },
  beginButtonRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  beginButtonRowMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "auto",
    marginTop: 0,
    gap: 10,
  },
  beginButton: {
    width: Platform.OS === "web" ? "15%" : 140,
    minWidth: 150,
    // marginLeft: 10,
  },
  actionItemButton: {
    color: "#fff",
    width: Platform.OS === "web" ? "15%" : 140,
    minWidth: 120,
    marginLeft: 10,
    backgroundColor: "#4CAF50",
    borderColor: "#4CAF50",
  },
  filterContainer: {
    marginTop: Platform.select({ web: 10, default: 0 }),
    // paddingHorizontal:24
  },
  summaryContainer: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 8,
    padding: 16,
    backgroundColor: "#fff",
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 12,
    color: "#212529",
  },
  resultItems: {
    marginLeft: 8,
  },
  gridContainer: {
    marginTop: 20,
    gap: 16,
    padding: 0,
  },
  gridRow: {
    flexDirection: isMobileView() ? "column" : "row",
    gap: 16,
  },
  gridCard: {
    flex: 1,
    // backgroundColor: "#fff",
    // borderWidth: 1,
    // borderColor: "#e0e0e0",
    // borderRadius: 8,
    padding: 16,
    // shadowColor: "#000",
    // shadowOffset: { width: 0, height: 1 },
    // shadowOpacity: 0.1,
    // shadowRadius: 3,
    // elevation: 2,
  },
  fullWidthCard: {
    // width: "100%",
    // backgroundColor: "#fff",
    // borderWidth: 1,
    // borderColor: "#e0e0e0",
    // borderRadius: 8,
    padding: 16,
    // shadowColor: "#000",
    // shadowOffset: { width: 0, height: 1 },
    // shadowOpacity: 0.1,
    // shadowRadius: 3,
    // elevation: 2,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 12,
    color: "#212529",
  },
  resultList: {
    marginLeft: 8,
  },
  resultItem: {
    fontSize: 16,
    marginBottom: 4,
    color: "#495057",
  },
  breakdownList: {
    gap: 16,
  },
  breakdownRow: {
    width: "100%",
  },
  breakdownLabelContainer: {
    width: "100%",
    gap: 8,
  },
  breakdownLabel: {
    fontSize: 16,
    color: "#495057",
    marginBottom: 4,
  },
  breakdownValue: {
    fontSize: 16,
    fontWeight: "600",
    color: "#0066cc",
    marginTop: 4,
  },
  actionPlanTable: {
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 4,
    overflow: "hidden",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  tableDataRow: {
    flexDirection: "row",
    minHeight: 48,
  },
  tableCell: {
    flex: 1,
    padding: 12,
    color: "#495057",
  },
  headerCell: {
    fontWeight: "600",
    color: "#212529",
  },
  chartContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 10,
  },
  chartSizeWrapper: {
    width: chartSize,
    alignSelf: "center",
  },
  legendContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: 10,
    gap: 12,
    paddingHorizontal: 8,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 8,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 4,
  },
  legendText: {
    fontSize: 14,
    color: "#495057",
  },
  centerLabel: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  centerLabelText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#212529",
  },
  centerLabelValue: {
    fontSize: 14,
    color: "#495057",
  },
  tableContainer: {
    flex: 1,
    marginTop: 16,
    marginBottom: 16,
    boxShadow: "rgba(0, 102, 204, 0.4) 0px 4px 12px 0px",
    backgroundColor: "#fff",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  overlayContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  overlayContent: {
    backgroundColor: "white",
    width: "90%",
    maxWidth: 1200,
    maxHeight: "90%",
    borderRadius: 8,
    padding: 20,
  },
  overlayHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  overlayTitle: {
    fontSize: 24,
    fontWeight: "500",
    color: "#212529",
  },
  closeIcon: {
    marginLeft: 10,
  },
  overlayScrollView: {
    maxHeight:
      Platform.OS === "web" ? Dimensions.get("window").height * 0.8 : "80%",
  },
  // Status message styles
  statusMessageContainer: {
    width: "100%",
    marginBottom: 12,
    zIndex: 10,
    position: "relative",
  },
  statusMessage: {
    padding: 14,
    borderRadius: 6,
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  errorMessage: {
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderLeftWidth: 5,
    borderLeftColor: "#dc3545",
  },
  errorText: {
    color: "#721c24",
    fontSize: 14,
    flex: 1,
  },
  successMessage: {
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderLeftWidth: 5,
    borderLeftColor: "#28a745",
  },
  successText: {
    color: "#155724",
    fontSize: 14,
    flex: 1,
  },
  /* Recommended Action Plan card styles */
  cardWrapper: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  cardHeader: {
    backgroundColor: '#034895',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  cardHeaderText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  emptyCard: {
    padding: 16,
    alignItems: 'center',
  },
  emptyText: {
    color: '#616161',
    fontSize: 14,
  },
  actionCard: {
    paddingVertical: 18,
    paddingHorizontal: 16,
    backgroundColor: '#fff',
  },
  actionRow: {
    flexDirection: isMobileView() ? 'column' : 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  actionLabel: {
    fontWeight: '700',
    color: '#2D2D2D',
    fontSize: 14,
    flexShrink: 0,
    ...(isMobileView() ? { marginBottom: 2 } : { width: 160, fontSize: 16 }),
  },
  actionValue: {
    flexShrink: 1,
    flexWrap: 'wrap',
    minWidth: 0,
    color: '#424242',
    fontSize: isMobileView() ? 13 : 16,
    fontWeight: '400',
  },
  cardDivider: {
    height: 1,
    backgroundColor: '#e9e9e9',
  },
});

export default SiteEvaluation;
